import React from 'react';

import classnames from 'classnames';

import { NavigationMenuDropdown } from './components/navigation-menu-dropdown/NavigationMenuDropdown';
import { NavigationMenuTrigger } from './components/navigation-menu-trigger/NavigationMenuTrigger';
import { useNavigationMenu } from './hooks/useNavigationMenu';

import './NavigationMenu.scss';

export interface OptionType {
	value: string;
	label: string;
}

interface NavigationMenuProps {
	id: string;
	ariaLabel: string;
	options: OptionType[];
	selectedValue: OptionType;
	onSelectChange: (selected: OptionType) => void;
	label?: string;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
	id,
	label,
	ariaLabel,
	options,
	selectedValue,
	onSelectChange,
	placeholder = 'Select an option',
	className,
	disabled = false
}) => {
	const {
		isOpen,
		searchTerm,
		focusedIndex,
		filteredOptions,
		displayText,
		containerRef,
		triggerRef,
		listboxRef,
		searchInputRef,
		handleToggle,
		handleOptionSelect,
		handleKeyDown,
		handleSearchChange,
		setFocusedIndex
	} = useNavigationMenu({
		options,
		selectedValue,
		onSelectChange,
		placeholder,
		disabled
	});

	const labelId = `${id}-label`;
	const listboxId = `${id}-listbox`;
	const searchId = `${id}-search`;

	return (
		<nav
			ref={containerRef}
			className={classnames('navigation-menu', className, {
				'navigation-menu--open': isOpen,
				'navigation-menu--disabled': disabled
			})}
			aria-label={ariaLabel}
		>
			{label && (
				<label id={labelId} htmlFor={id} className="navigation-menu__label">
					{label}
				</label>
			)}

			<div className="navigation-menu__container">
				<NavigationMenuTrigger
					id={id}
					isOpen={isOpen}
					disabled={disabled}
					ariaLabel={ariaLabel}
					labelId={label ? labelId : undefined}
					displayText={displayText}
					onClick={handleToggle}
					onKeyDown={handleKeyDown}
					triggerRef={triggerRef}
					ariaHaspopup="true"
					ariaExpanded={isOpen}
					ariaControls={isOpen ? listboxId : undefined}
				>
					{isOpen && (
						<NavigationMenuDropdown
							listboxId={listboxId}
							searchId={searchId}
							ariaLabel={ariaLabel}
							searchTerm={searchTerm}
							filteredOptions={filteredOptions}
							focusedIndex={focusedIndex}
							selectedValue={selectedValue}
							onSearchChange={handleSearchChange}
							onOptionSelect={handleOptionSelect}
							onFocusedIndexChange={setFocusedIndex}
							onKeyDown={handleKeyDown}
							searchInputRef={searchInputRef}
							listboxRef={listboxRef}
						/>
					)}
				</NavigationMenuTrigger>
			</div>
		</nav>
	);
};
